import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../../__generated__/schema-lite';
import { useCurrentAccount } from '../../account/AccountContext';
import { MonthlyCreditsBars } from '../../components/MonthlyCreditsBars';
import { PackageSelector } from '../../components/PackageSelector';

type MonthUsage = { month: string; monthUnix: number; credits: number; calls: number; durationSec: number };
type AccountUsage = {
  monthsBack: number;
  monthly: MonthUsage[];
  totals: { credits: number; calls: number; durationSec: number };
};

/** The customer's own line usage — NOT the shared ElevenLabs plan (that is
 *  workspace-wide and belongs to the operator). Credits here are this account's own
 *  calls summed from the DB, in ElevenLabs' credit unit; there is no per-model split
 *  at the account level (the provider only breaks usage down by model workspace-wide). */
export default function CustomerSubscriptionPage() {
  const { t, i18n } = useTranslation();
  const { accountId } = useCurrentAccount();
  const crud = useClientQueries(schema);
  const q = crud.$procs.accountUsage.useQuery({ args: { input: { accountId, monthsBack: 12 } } });

  const usage = useMemo<AccountUsage | null>(() => {
    if (typeof q.data !== 'string') return null;
    try {
      return JSON.parse(q.data) as AccountUsage;
    } catch {
      return null;
    }
  }, [q.data]);

  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  return (
    <div className="h-full overflow-y-auto px-4 pt-6 pb-24">
      <div className="mx-auto max-w-md space-y-5">
        <header>
          <h1 className="text-xl font-semibold">{t('subscription.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('subscription.customerSubtitle')}</p>
        </header>

        {/* Buy-more selector — first, so it is the thing you see, and independent of
         *  the usage query so it shows even while usage is loading or unavailable. */}
        <PackageSelector />

        {q.isPending ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : q.isError || !usage ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('subscription.loadError')}</p>
        ) : (
          <>
            {/* Own-usage summary — the whole period at a glance. */}
            <div className="grid grid-cols-3 gap-2">
              <Stat label={t('subscription.totalCredits')} value={nf.format(usage.totals.credits)} />
              <Stat label={t('subscription.totalCalls')} value={nf.format(usage.totals.calls)} />
              <Stat
                label={t('subscription.talkTime')}
                value={t('subscription.minutes', { count: Math.round(usage.totals.durationSec / 60) })}
              />
            </div>

            {/* Monthly credits — single series, so no legend. Tapping a bar opens
             *  that month's detail (credits, calls, talk time). */}
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-3 text-sm font-medium">{t('subscription.monthlyCredits')}</h2>
              <MonthlyCreditsBars
                months={usage.monthly.map((m) => m.monthUnix)}
                series={[
                  {
                    key: 'credits',
                    label: t('subscription.totalCredits'),
                    values: usage.monthly.map((m) => m.credits),
                  },
                ]}
                renderDetail={(mi) => <MonthDetail t={t} nf={nf} m={usage.monthly[mi]} />}
              />
            </div>

            <p className="text-xs text-muted-foreground">{t('subscription.creditNote')}</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Per-month breakdown shown when a bar is tapped — richer than the hover tooltip:
 *  that month's credits, call count and talk time. */
function MonthDetail({
  t,
  nf,
  m,
}: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  nf: Intl.NumberFormat;
  m: MonthUsage | undefined;
}) {
  if (!m) return null;
  const row = (label: string, value: string) => (
    <li className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </li>
  );
  return (
    <ul className="space-y-1 text-sm">
      {row(t('subscription.totalCredits'), nf.format(m.credits))}
      {row(t('subscription.totalCalls'), nf.format(m.calls))}
      {row(t('subscription.talkTime'), t('subscription.minutes', { count: Math.round(m.durationSec / 60) }))}
    </ul>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3 text-center">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}
